import type { Action, Middleware, UnknownAction } from "redux";
import type { ReduxActionMeta } from "../types/ActionMeta";
import type { ISocketMiddlewareAction } from "./websocket";

export const REALTIME_SOCKET_REQUEST = "REALTIME_SOCKET_REQUEST";
export const DEFAULT_REALTIME_REQUEST_TIMEOUT_MS = 15 * 60 * 1000;

export type RealtimeRequestAction<P = unknown, U = unknown> = Action & {
    type: typeof REALTIME_SOCKET_REQUEST;
    realtimeRequest: {
        type: string;
        responseTypePrefix: string;
        requestId?: string;
        timeoutMs?: number;
        onRequest?: (payload: U) => void;
        onUpdate?: (payload: U) => void;
        meta?: ReduxActionMeta;
    };
    payload?: P;
};

export type RealtimeRequestOptions<P = unknown, U = unknown> = {
    type: string;
    responseTypePrefix: string;
    payload?: P;
    requestId?: string;
    timeoutMs?: number;
    onRequest?: (payload: U) => void;
    onUpdate?: (payload: U) => void;
    meta?: ReduxActionMeta;
};

type PendingRealtimeRequest = {
    requestType: string;
    updateType: string;
    successType: string;
    errorType: string;
    timeoutId?: ReturnType<typeof setTimeout>;
    onRequest?: (payload: unknown) => void;
    onUpdate?: (payload: unknown) => void;
    resolve: (payload: unknown) => void;
    reject: (error: Error) => void;
};

let requestCounter = 0;
const pendingByResponseType = new Map<string, PendingRealtimeRequest>();

export const createRealtimeRequestId = (): string => {
    requestCounter += 1;
    return `${Date.now().toString(36)}-${requestCounter.toString(36)}`;
};

const buildResponseTypes = (prefix: string, requestId: string): string[] => [
    `${prefix}/${requestId}/REQUEST`,
    `${prefix}/${requestId}/UPDATE`,
    `${prefix}/${requestId}/SUCCESS`,
    `${prefix}/${requestId}/ERROR`,
];

const cleanupPendingRequest = (pending: PendingRealtimeRequest): void => {
    if (pending.timeoutId) {
        clearTimeout(pending.timeoutId);
    }
    pendingByResponseType.delete(pending.requestType);
    pendingByResponseType.delete(pending.updateType);
    pendingByResponseType.delete(pending.successType);
    pendingByResponseType.delete(pending.errorType);
};

const getRealtimeErrorMessage = (payload: unknown): string => {
    if (payload && typeof payload === "object") {
        const errorPayload = payload as {
            message?: unknown;
            code?: unknown;
            response?: { error?: unknown; message?: unknown };
        };
        if (
            typeof errorPayload.message === "string" &&
            errorPayload.message.trim()
        ) {
            return errorPayload.message;
        }
        if (
            typeof errorPayload.response?.message === "string" &&
            errorPayload.response.message.trim()
        ) {
            return errorPayload.response.message;
        }
        if (
            typeof errorPayload.response?.error === "string" &&
            errorPayload.response.error.trim()
        ) {
            return errorPayload.response.error;
        }
        if (typeof errorPayload.code === "string" && errorPayload.code.trim()) {
            return `analysis.error.${errorPayload.code}`;
        }
    }
    return "Realtime request failed.";
};

const isRealtimeRequestAction = (
    action: UnknownAction
): action is RealtimeRequestAction<unknown, unknown> =>
    action?.type === REALTIME_SOCKET_REQUEST &&
    !!(action as RealtimeRequestAction).realtimeRequest?.type &&
    !!(action as RealtimeRequestAction).realtimeRequest?.responseTypePrefix;

export const createRealtimeRequestAction = <P = unknown, U = unknown>({
    type,
    responseTypePrefix,
    payload,
    requestId,
    timeoutMs,
    onRequest,
    onUpdate,
    meta,
}: RealtimeRequestOptions<P, U>): RealtimeRequestAction<P, U> => ({
    type: REALTIME_SOCKET_REQUEST,
    realtimeRequest: {
        type,
        responseTypePrefix,
        requestId,
        timeoutMs,
        onRequest,
        onUpdate,
        meta,
    },
    payload,
});

export const realtimeRequestMiddleware: Middleware =
    () => (next) => (action: UnknownAction) => {
        if (isRealtimeRequestAction(action)) {
            const requestId =
                action.realtimeRequest.requestId || createRealtimeRequestId();
            const responseTypes = buildResponseTypes(
                action.realtimeRequest.responseTypePrefix,
                requestId
            );
            const [requestType, updateType, successType, errorType] =
                responseTypes;

            const promise = new Promise<unknown>((resolve, reject) => {
                const pending: PendingRealtimeRequest = {
                    requestType,
                    updateType,
                    successType,
                    errorType,
                    onRequest: action.realtimeRequest.onRequest,
                    onUpdate: action.realtimeRequest.onUpdate,
                    resolve,
                    reject,
                };
                const timeoutMs =
                    action.realtimeRequest.timeoutMs ??
                    DEFAULT_REALTIME_REQUEST_TIMEOUT_MS;

                if (timeoutMs > 0) {
                    pending.timeoutId = setTimeout(() => {
                        cleanupPendingRequest(pending);
                        reject(new Error("Realtime request timed out."));
                    }, timeoutMs);
                }

                pendingByResponseType.set(requestType, pending);
                pendingByResponseType.set(updateType, pending);
                pendingByResponseType.set(successType, pending);
                pendingByResponseType.set(errorType, pending);
            });

            const socketAction: ISocketMiddlewareAction<unknown> = {
                type: action.realtimeRequest.type,
                socket: { types: responseTypes },
                payload: action.payload,
                ...(action.realtimeRequest.meta
                    ? { meta: action.realtimeRequest.meta }
                    : {}),
            };

            next(socketAction as unknown as UnknownAction);
            return promise;
        }

        const pending = pendingByResponseType.get(action.type);
        if (!pending) {
            return next(action);
        }

        const result = next(action);

        if (action.type === pending.requestType) {
            pending.onRequest?.(action.payload);
            return result;
        }

        if (action.type === pending.updateType) {
            pending.onUpdate?.(action.payload);
            return result;
        }

        cleanupPendingRequest(pending);

        if (action.type === pending.successType) {
            pending.resolve(action.payload);
        } else {
            pending.reject(new Error(getRealtimeErrorMessage(action.payload)));
        }

        return result;
    };
